import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { ConsultationFormModal } from '../features/iso45001/consultation/ConsultationFormModal';
import {
  createWorkerConsultation,
  fetchWorkerConsultations,
  transitionWorkerConsultationStatus,
  updateWorkerConsultation,
} from '../features/iso45001/consultation/consultationApi';
import type { WorkerConsultationSummary } from '../features/iso45001/consultation/types';
import {
  MECHANISM_LABEL,
  STATUS_LABEL,
  mechanismBadgeClass,
  statusBadgeClass,
} from '../features/iso45001/consultation/consultationUtils';

const NEXT_STATUS: Partial<
  Record<WorkerConsultationSummary['status'], WorkerConsultationSummary['status']>
> = {
  planificado: 'realizado',
  realizado: 'cerrado',
};

function formatDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-AR');
}

export function WorkerConsultationsPage() {
  const { canEdit } = usePlatformAccessLevel('iso45001.consultation.access');
  const [items, setItems] = useState<WorkerConsultationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<WorkerConsultationSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchWorkerConsultations({ page, pageSize: 20 });
      setItems(list.data);
      setTotalPages(list.pagination.pages);
    } catch {
      setError('No se pudieron cargar los registros de consulta.');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  const advanceStatus = async (item: WorkerConsultationSummary) => {
    const next = NEXT_STATUS[item.status];
    if (!next) return;
    await transitionWorkerConsultationStatus(item.id, next);
    await load();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Consulta y participación de trabajadores</h1>
          <p className="text-muted-foreground text-sm">ISO 45001 5.4 — evidencia de consulta</p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            Registrar consulta
          </button>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 font-medium">Fecha</th>
              <th className="text-left p-3 font-medium">Tema</th>
              <th className="text-left p-3 font-medium">Mecanismo</th>
              <th className="text-left p-3 font-medium">Encuesta</th>
              <th className="text-left p-3 font-medium">Estado</th>
              {canEdit && <th className="text-left p-3 font-medium">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={canEdit ? 6 : 5} className="p-6 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 6 : 5} className="p-6 text-center text-muted-foreground">
                  No hay registros de consulta.
                </td>
              </tr>
            )}
            {!loading &&
              items.map((item) => (
                <tr key={item.id} className="border-t border-border hover:bg-muted/30">
                  <td className="p-3 whitespace-nowrap">{formatDate(item.date)}</td>
                  <td className="p-3">
                    <div className="font-medium">{item.topic}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2">{item.outcome}</div>
                  </td>
                  <td className="p-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${mechanismBadgeClass(item.mechanism)}`}
                    >
                      {MECHANISM_LABEL[item.mechanism]}
                    </span>
                  </td>
                  <td className="p-3">
                    {item.linkedSurvey ? (
                      <Link
                        to={`/surveys/${item.linkedSurvey.id}/statistics`}
                        className="text-primary hover:underline"
                      >
                        {item.linkedSurvey.title}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(item.status)}`}
                    >
                      {STATUS_LABEL[item.status]}
                    </span>
                  </td>
                  {canEdit && (
                    <td className="p-3 space-x-2 whitespace-nowrap">
                      {item.status !== 'cerrado' && (
                        <>
                          <button
                            type="button"
                            className="text-primary hover:underline text-xs"
                            onClick={() => setEditItem(item)}
                          >
                            Editar
                          </button>
                          {NEXT_STATUS[item.status] && (
                            <button
                              type="button"
                              className="text-primary hover:underline text-xs"
                              onClick={() => void advanceStatus(item)}
                            >
                              → {STATUS_LABEL[NEXT_STATUS[item.status]!]}
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      <ConsultationFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Registrar consulta a trabajadores"
        onSubmit={async (payload) => {
          await createWorkerConsultation(payload);
          await load();
        }}
      />

      <ConsultationFormModal
        open={editItem !== null}
        onClose={() => setEditItem(null)}
        title="Editar registro de consulta"
        initial={editItem ?? undefined}
        onSubmit={async (payload) => {
          if (!editItem) return;
          await updateWorkerConsultation(editItem.id, payload);
          await load();
        }}
      />
    </div>
  );
}
